import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Divider, 
  Alert 
} from '@mui/material';
import { checkUserAccess } from '../../api/auth';
import { AuthContext } from '../../context/AuthContext';

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});

const Login = () => {
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setError('');

    try {
      const result = await checkUserAccess(values.email);
      
      if (result.success && result.data) {
        // Assuming the API returns user data with role information
        login(result.data);
        
        // Redirect based on user role
        if (result.data.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (result.data.role === 'manager') {
          navigate('/manager/dashboard');
        } else if (result.data.role === 'investor') {
          navigate('/investor/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError('User not found. Please register or check your email.');
      }
    } catch (error) {
      setError('An error occurred. Please try again later.');
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="auth-container">
      <Card className="auth-card">
        <CardContent>
          <Box className="auth-header">
            <img 
              src="/logo.png" 
              alt="HFRConnect Logo" 
              className="auth-logo"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/200x80?text=HFRConnect';
              }}
            />
            <Typography variant="h5" component="h1" gutterBottom>
              Log in to HFRConnect
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Formik
            initialValues={{ email: '' }}
            validationSchema={loginSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values, handleChange, handleBlur }) => (
              <Form>
                <Box className="form-field">
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email Address"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    variant="outlined"
                  />
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  className="auth-submit"
                >
                  {isSubmitting ? 'Logging in...' : 'Log In'}
                </Button>
              </Form>
            )}
          </Formik>

          <Divider className="auth-divider" />

          <Box className="auth-footer">
            <Typography variant="body2">
              Don't have an account?{' '}
              <Link to="/register" style={{ color: '#1976d2' }}>
                Register
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
